'use client';

import { useEffect, useState } from 'react';
import { BalderWordmark } from './balder-wordmark';
// Only a string constant and a pure function - no tokens, no shared UI, so it does not
// compromise this screen's "renders when everything else failed" guarantee.
import { SUPPORT_EMAIL } from './support-mailto';

/**
 * Tier 1 - the app itself failed. Rendered by `global-error.tsx` (above the root layout)
 * and by `ClientErrorBoundary` (above the providers), so it deliberately owns no design
 * tokens, no @balder/ui, no icon library: hard-coded hex, system-font fallback, inline
 * SVG, native button. It has to hold up with the stylesheet missing entirely.
 */
export const FatalScreen = ({ referenceId }: { referenceId?: string }) => {
  const subject = referenceId ? `Balder Cloud error ${referenceId}` : 'Balder Cloud error';

  return (
    <div
      style={{
        position: 'relative',
        // Fills the flex parent rather than forcing 100vh, so it centres the same way
        // Tier 2 does whatever container it is rendered into.
        flex: 1,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Same 48/120 as ErrorState: the block sits slightly above centre.
        padding: '48px 24px 120px',
        background: '#fff',
        color: '#050505',
        fontFamily: "'Geist', system-ui, sans-serif",
      }}
    >
      <BalderWordmark
        style={{ position: 'absolute', top: 32, left: 32, height: 14, width: 'auto' }}
      />
      <div style={{ width: 340, maxWidth: '100%', textAlign: 'center' }}>
        <div
          style={{
            width: 40,
            height: 40,
            margin: '0 auto',
            borderRadius: 8,
            background: '#fafafa',
            border: '1px solid #e4e4e7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#a1a1aa',
          }}
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <h1
          style={{
            font: "600 17px/1.4 'Geist', system-ui, sans-serif",
            letterSpacing: '-0.005em',
            margin: '18px 0 0',
          }}
        >
          Balder Cloud didn&apos;t start
        </h1>
        <p
          style={{
            font: "400 13px/1.65 'Geist', system-ui, sans-serif",
            color: '#71717a',
            margin: '7px 0 0',
          }}
        >
          The app failed to load in this browser. Reloading usually fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 22,
            height: 44,
            padding: '0 20px',
            borderRadius: 8,
            border: 'none',
            background: '#18181b',
            color: '#fff',
            font: "500 14px 'Geist', system-ui, sans-serif",
            cursor: 'pointer',
          }}
        >
          Reload Balder Cloud
        </button>
        <p
          style={{
            font: "400 12px/1.9 'Geist', system-ui, sans-serif",
            color: '#71717a',
            marginTop: 34,
          }}
        >
          {referenceId && (
            <>
              We&apos;ve been notified. Reference{' '}
              <span
                style={{
                  fontFamily: "'Geist Mono', ui-monospace, monospace",
                  fontSize: 12,
                  color: '#18181b',
                }}
              >
                {referenceId}
              </span>
              <CopyButton value={referenceId} label="Reference" />
              <br />
            </>
          )}
          Still stuck? Email{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`}
            style={{ color: '#18181b' }}
          >
            {SUPPORT_EMAIL}
          </a>
          <CopyButton value={SUPPORT_EMAIL} label="Support email" />
        </p>
      </div>
    </div>
  );
};

// Local twin of the shared CopyButton: this screen cannot import from error-state.tsx,
// which pulls in @balder/ui and lucide - the exact dependencies it must survive without.
const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
      onClick={() => {
        // writeText rejects under a restrictive permissions policy; never claim success
        // then. The value stays on screen as text, so it is selectable by hand regardless.
        void (async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch {
            /* clipboard unavailable */
          }
        })();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: -2,
        width: 40,
        height: 40,
        margin: '-14px -14px -14px -9px',
        border: 'none',
        background: 'none',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {copied ? (
          <path d="M20 6 9 17l-5-5" />
        ) : (
          <>
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </>
        )}
      </svg>
    </button>
  );
};
